import { Avatar, Box, Stack, Typography, Modal, Tooltip, IconButton } from "@mui/material";
import { stringAvatar } from "utils/stringAvatar";
import { parseComment } from "./utils/commentParser";
import { WorkspaceUser } from "../../types/workspaceUser";
import { useGetVirtualTourAnnotationsAttachmentsQuery } from "api/virtual-tour/queries/useGetVirtualTourAnnotationAttachmentsQuery";
import { useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteAnnotationCommentMutation } from "api/virtual-tour/mutations/useDeleteAnnotationCommentMutation";
import { useAuth0 } from "@auth0/auth0-react";
import { useDeleteAnnotationMutation } from "api/virtual-tour/mutations/useDeleteAnnotationMutation";
import { toast } from "react-toastify";
import { commentStyles } from "./commentStyles";

interface TextAnnotationProps {
  annotation: VirtualTourAnnotation;
  users: WorkspaceUser[];
  tourId: string;
}

const TextAnnotation = ({
  annotation,
  users,
  tourId
}: TextAnnotationProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { data: annotationSignedUrls } = useGetVirtualTourAnnotationsAttachmentsQuery(annotation.id);

  if (!annotation.message) return null;

  const { user } = useAuth0();

  const { mutate: deleteAnnotation, isPending: isDeletingAnnotation } = useDeleteAnnotationMutation();

  // Find the user's picture from users array
  const annotationUser = users.find(u => u.user.id === annotation.author.id);
  const picture = annotationUser?.user.picture;

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
  };

  const isAuthorOfAnnotation = annotation.author.id === user?.sub;

  const handleDeleteAnnotation = () => {
    deleteAnnotation({
      annotationId: annotation.id,
      tourId
    }, {
      onSuccess: () => {
        toast.success("Annotation deleted");
      },
      onError: () => {
        toast.error("Failed to delete annotation");
      }
    });
  };

  return (
    <>
      <Box sx={{
        height: 'max-content',
        width: '100%', // Always full width
        maxWidth: '100%',
        borderLeft: '3px solid #58a6ff',
        pl: 1,
        mb: 2, // Add margin bottom for spacing
      }}>
        <Stack direction="row" spacing={2} sx={{
          alignItems: 'center',
          mb: 1.5,
          px: .5
        }}>
          <Avatar
            src={picture}
            {...(!picture && stringAvatar(annotation.author.name))}
            sx={{
              width: 40, // Larger avatar for main annotation
              height: 40,
              fontSize: '1rem',
              flexShrink: 0,
              border: '2px solid #58a6ff',
              boxShadow: '0 0 0 2px rgba(88, 166, 255, 0.2)' // Subtle glow effect
            }}
          />
          <Stack spacing={0.5}>
            <Typography
              variant="body1" // Slightly larger text
              sx={{
                color: '#fff',
                fontWeight: 600,
                lineHeight: 1.2
              }}
            >
              {annotation.author.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}
            >
              {new Date(annotation.createdAt).toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{
          padding: '1rem', // Slightly more padding
          backgroundColor: '#313131',
          borderRadius: '5px',
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '1rem', // Slightly larger font
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          hyphens: 'auto',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          border: '1px solid #414141',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, #58a6ff 0%, transparent 100%)',
          }
        }}>
          {parseComment(annotation.message, users, annotation, annotationSignedUrls, handleImageClick)}
          {isAuthorOfAnnotation && (
            <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
              {/* { comment. } */}
              <Stack direction="row" spacing={1}>
                <Typography variant="caption" color="rgba(255, 255, 255, 0.5)">
                  {(new Date(annotation.createdAt)).toLocaleDateString()}
                </Typography>
              </Stack>
              <Stack direction="row">
                {/* TODO: edit is currently disabled since i'll have to 
                figure out how to parse and put it into lexical editor's format appropriately*/}
                {/* <Tooltip title="Edit Comment">
                  <IconButton sx={commentStyles.commentActionIconButton} onClick={handleEditComment}>
                    <EditIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Delete Comment">
                  <IconButton sx={commentStyles.commentActionIconButton} onClick={handleDeleteAnnotation}>
                    <DeleteIcon sx={{ fontSize: '1rem' }} color="error" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Image Modal */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            width: '75vw',  // Set base width
            height: '80vh', // Set base height
            bgcolor: 'transparent',
            outline: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage || ''}
            alt="Enlarged view"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default TextAnnotation;
